import { Controller } from "@hotwired/stimulus"

// Controller for the fleet calendar filter panel
// Handles filter UI interactions
export default class extends Controller {
  static targets = [
    "panel",
    "form",
    "searchInput",
    "categoryButton",
    "categoryContent",
    "allFleetsCheckbox",
    "fleetCheckbox",
    "allSitesCheckbox",
    "siteCheckbox",
    "allEmployeesCheckbox",
    "employeeCheckbox",
    "filterItem",
    "plannedOnlyCheckbox",
  ]

  connect() {
    // Close panel when clicking outside
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    event.stopPropagation()
    this.panelTarget.classList.toggle("hidden")
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  selectCategory(event) {
    const category = event.currentTarget.dataset.category

    // Update button styles
    this.categoryButtonTargets.forEach((btn) => {
      if (btn.dataset.category === category) {
        btn.classList.remove("text-gray-600", "hover:bg-gray-100")
        btn.classList.add("bg-gray-100", "text-gray-900", "font-medium")
      } else {
        btn.classList.add("text-gray-600", "hover:bg-gray-100")
        btn.classList.remove("bg-gray-100", "text-gray-900", "font-medium")
      }
    })

    // Show/hide category content
    this.categoryContentTargets.forEach((content) => {
      if (content.dataset.category === category) {
        content.classList.remove("hidden")
      } else {
        content.classList.add("hidden")
      }
    })

    // Clear search when switching categories
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      this.search()
    }
  }

  search() {
    const query = this.searchInputTarget.value.toLowerCase().trim()

    this.filterItemTargets.forEach((item) => {
      // Only filter items in visible category
      if (item.closest("[data-category]").classList.contains("hidden")) {
        return
      }

      const searchText = item.dataset.searchText || ""
      if (query === "" || searchText.includes(query)) {
        item.classList.remove("hidden")
      } else {
        item.classList.add("hidden")
      }
    })
  }

  toggleAll(event) {
    const checkbox = event.currentTarget
    // Determine which category we're in
    if (this.hasAllFleetsCheckboxTarget && checkbox === this.allFleetsCheckboxTarget) {
      this.fleetCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
    } else if (this.hasAllSitesCheckboxTarget && checkbox === this.allSitesCheckboxTarget) {
      this.siteCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
    } else if (this.hasAllEmployeesCheckboxTarget && checkbox === this.allEmployeesCheckboxTarget) {
      this.employeeCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
    }
  }

  updateSelection(event) {
    const checkbox = event.currentTarget

    // If any individual checkbox is checked, uncheck the "all" checkbox
    if (checkbox.checked) {
      if (this.fleetCheckboxTargets.includes(checkbox) && this.hasAllFleetsCheckboxTarget) {
        this.allFleetsCheckboxTarget.checked = false
      } else if (this.siteCheckboxTargets.includes(checkbox) && this.hasAllSitesCheckboxTarget) {
        this.allSitesCheckboxTarget.checked = false
      } else if (
        this.employeeCheckboxTargets.includes(checkbox) &&
        this.hasAllEmployeesCheckboxTarget
      ) {
        this.allEmployeesCheckboxTarget.checked = false
      }
    } else {
      // If no checkboxes are checked in a category, check the "all" checkbox
      if (this.fleetCheckboxTargets.includes(checkbox)) {
        const anyChecked = this.fleetCheckboxTargets.some((cb) => cb.checked)
        if (!anyChecked && this.hasAllFleetsCheckboxTarget) {
          this.allFleetsCheckboxTarget.checked = true
        }
      } else if (this.siteCheckboxTargets.includes(checkbox)) {
        const anyChecked = this.siteCheckboxTargets.some((cb) => cb.checked)
        if (!anyChecked && this.hasAllSitesCheckboxTarget) {
          this.allSitesCheckboxTarget.checked = true
        }
      } else if (this.employeeCheckboxTargets.includes(checkbox)) {
        const anyChecked = this.employeeCheckboxTargets.some((cb) => cb.checked)
        if (!anyChecked && this.hasAllEmployeesCheckboxTarget) {
          this.allEmployeesCheckboxTarget.checked = true
        }
      }
    }
  }

  clearAll() {
    // Uncheck all individual checkboxes
    this.fleetCheckboxTargets.forEach((cb) => {
      cb.checked = false
    })
    this.siteCheckboxTargets.forEach((cb) => {
      cb.checked = false
    })
    this.employeeCheckboxTargets.forEach((cb) => {
      cb.checked = false
    })

    // Uncheck the remaining filter groups (vehicle type, status, trade, task type, task status)
    const extraGroups =
      'input[name="fleet_types[]"], input[name="fleet_statuses[]"], input[name="trade_ids[]"], input[name="task_type_ids[]"], input[name="task_statuses[]"]'
    this.element.querySelectorAll(extraGroups).forEach((cb) => {
      cb.checked = false
    })

    // Check all "all" checkboxes
    if (this.hasAllFleetsCheckboxTarget) this.allFleetsCheckboxTarget.checked = true
    if (this.hasAllSitesCheckboxTarget) this.allSitesCheckboxTarget.checked = true
    if (this.hasAllEmployeesCheckboxTarget) this.allEmployeesCheckboxTarget.checked = true

    // Reset weekend filter to show_all
    const radioButtons = this.element.querySelectorAll('input[name="hide_weekend"]')
    radioButtons.forEach((radio) => {
      radio.checked = radio.value === "show_all"
    })

    // Uncheck the planned-only toggle
    if (this.hasPlannedOnlyCheckboxTarget) this.plannedOnlyCheckboxTarget.checked = false
  }
}
